import { Button, Flex, Text, Tooltip } from '@mantine/core';
import type { ReactNode } from 'react';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SlotCard } from './EmptySignerSlot.styled';

interface Props {
	slotNumber: number;
	totalSlots: number;
	onClaim?: () => void;
	onAssign: () => void;
	onInviteAuthorisedSignatory?: () => void;
	inviteDisabled?: boolean;
	inviteDisabledReason?: string;
	isClaimPending?: boolean;
	// When the slot is being assigned, the assign form renders inside this same
	// card (in place of the action buttons) so the slot and form read as one card.
	assignForm?: ReactNode;
}

export const EmptySignerSlot = ({
	slotNumber,
	totalSlots,
	onClaim,
	onAssign,
	onInviteAuthorisedSignatory,
	inviteDisabled,
	inviteDisabledReason,
	isClaimPending,
	assignForm,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<SlotCard>
			<Flex direction="column" gap="1rem">
				<Flex direction="column" style={{ flex: 1, minWidth: 0 }} gap="0.125rem">
					<Text fw={600} size="0.875rem" lh="1.5rem" c={defaultTheme.v3.colors.black[0]}>
						{tMipManage('documents_page.signer.empty_slot.title')}
					</Text>
					<Text size="0.875rem" c={defaultTheme.v3.colors.dark[0]} lh="1.25rem">
						{tMipManage('documents_page.signer.empty_slot.subtitle', {
							slot: slotNumber,
							total: totalSlots,
						})}
					</Text>
				</Flex>

				{assignForm ?? (
					<Flex direction="column" gap="0.5rem" align="stretch">
						{onClaim && (
							<Button
								data-testid={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_CLAIM_BUTTON}
								variant="outline"
								size="md"
								loading={isClaimPending}
								disabled={isClaimPending}
								onClick={onClaim}
								styles={{ root: { fontWeight: 400 } }}
							>
								{tMipManage('documents_page.signer.empty_slot.claim')}
							</Button>
						)}
						<Button
							variant="outline"
							size="md"
							onClick={onAssign}
							styles={{
								root: {
									fontWeight: 400,
									borderColor: defaultTheme.v3.colors.gray[4],
									color: defaultTheme.v3.colors.black[0],
									backgroundColor: defaultTheme.v3.colors.white[0],
								},
							}}
						>
							{tMipManage('documents_page.signer.empty_slot.assign')}
						</Button>
						{onInviteAuthorisedSignatory && (
							// A disabled button fires no mouse events, so the tooltip needs a
							// wrapper element to hover (mirrors ExpandablePersonCard).
							<Tooltip
								label={inviteDisabledReason}
								disabled={!inviteDisabledReason || !inviteDisabled}
								withinPortal
								multiline
								w={260}
							>
								<span style={{ display: 'block' }}>
									<Button
										data-testid={
											TEST_ELEMENT_IDS.DOCUMENT_SIGNER_INVITE_AUTHORISED_SIGNATORY_BUTTON
										}
										variant="outline"
										size="md"
										fullWidth
										disabled={inviteDisabled}
										onClick={onInviteAuthorisedSignatory}
										styles={{
											root: {
												fontWeight: 400,
												// Skip the custom palette when disabled so
												// Mantine's own greyed-out styling shows through.
												...(inviteDisabled
													? {}
													: {
															borderColor:
																defaultTheme.v3.colors.gray[4],
															color: defaultTheme.v3.colors.black[0],
															backgroundColor:
																defaultTheme.v3.colors.white[0],
														}),
											},
										}}
									>
										{tMipManage(
											'documents_page.signer.empty_slot.invite_authorised_signatory'
										)}
									</Button>
								</span>
							</Tooltip>
						)}
					</Flex>
				)}
			</Flex>
		</SlotCard>
	);
};
